import React, { useEffect, useState, useCallback } from "react";
import {
  Container, Typography, Box, Card, CardContent, CardActions,
  Button, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Switch,
  FormControlLabel, Chip, Avatar, Fab,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { events as eventsApi, locations as locationsApi } from "../api/endpoints";
import type { Event, EventRSVPStatus } from "../types";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useUserSettings } from "../context/UserSettingsContext";
import LocationPicker, { EMPTY_LOCATION, LocationFormState } from "../components/LocationPicker";
import { localDatetimeToUTC, formatInTimezone, countryNameFromCode } from "../utils/locationUtils";

// Matches EventDetail's status accent so the list and detail pages agree.
const STATUS_ACCENTS: Record<string, string> = {
  scheduled: "#2563EB",
  ongoing: "#059669",
  completed: "#6B7280",
  cancelled: "#DC2626",
};

const RSVP_COLORS: Record<EventRSVPStatus, "success" | "primary" | "warning" | "inherit"> = {
  going: "success",
  interested: "primary",
  maybe: "warning",
  declined: "inherit",
};

export default function EventsPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { timezone, countryCode } = useUserSettings();
  const navigate = useNavigate();
  const [list, setList] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [upcomingOnly, setUpcomingOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", start_time: "", end_time: "" });
  const [locationForm, setLocationForm] = useState<LocationFormState>(EMPTY_LOCATION);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createFieldErrors, setCreateFieldErrors] = useState<Record<string, string>>({});

  const [rsvpMap, setRsvpMap] = useState<Map<number, EventRSVPStatus>>(new Map());

  const RSVP_LABELS: Record<string, string> = {
    going: t("events.going"),
    interested: t("events.interested"),
    maybe: t("events.maybe"),
  };

  const load = useCallback(async (p: number, upcoming: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const res = await eventsApi.list({ page: p, limit: 20, upcoming: upcoming || undefined });
      if (p === 1) setList(res.data ?? []);
      else setList((prev) => [...prev, ...(res.data ?? [])]);
      if (res.meta) setTotalPages(res.meta.total_pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.noResults"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    setPage(1);
    load(1, upcomingOnly);
  }, [upcomingOnly, load]);

  const handleRSVP = async (eventId: number, status: EventRSVPStatus) => {
    try {
      await eventsApi.rsvp(eventId, status);
      setRsvpMap((m) => new Map(m).set(eventId, status));
      setList((prev) => prev.map((ev) =>
        ev.id === eventId
          ? { ...ev, participants_count: (ev.participants_count ?? 0) + (status === "going" ? 1 : 0) }
          : ev
      ));
    } catch { /* ignore */ }
  };

  const validateCreate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.title.trim()) errs.title = t("eventValidation.titleRequired");
    else if (form.title.trim().length < 2) errs.title = t("eventValidation.titleTooShort");
    if (!form.start_time) errs.start_time = t("eventValidation.startTimeRequired");
    if (form.end_time && form.start_time && new Date(form.end_time) <= new Date(form.start_time))
      errs.end_time = t("eventValidation.endBeforeStart");
    if (!locationForm.country_code) errs.country = t("eventValidation.countryRequired");
    setCreateFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleOpenCreate = () => {
    setForm({ title: "", description: "", start_time: "", end_time: "" });
    setLocationForm({ ...EMPTY_LOCATION, country_code: countryCode || "" });
    setCreateError(null);
    setCreateFieldErrors({});
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!validateCreate()) return;
    setCreateLoading(true);
    setCreateError(null);
    try {
      let locationId: number | null = null;
      if (locationForm.country_code) {
        const loc = await locationsApi.create({
          address_line1: locationForm.address_line1 || locationForm.city || countryNameFromCode(locationForm.country_code),
          city: locationForm.city || null,
          state_province: locationForm.state_province || null,
          country_code: locationForm.country_code,
          latitude: locationForm.latitude ?? null,
          longitude: locationForm.longitude ?? null,
        });
        locationId = loc?.id ?? null;
      }
      await eventsApi.create({
        title: form.title,
        description: form.description,
        start_time: localDatetimeToUTC(form.start_time, timezone),
        end_time: form.end_time ? localDatetimeToUTC(form.end_time, timezone) : undefined,
        location_id: locationId,
      });
      setCreateOpen(false);
      setForm({ title: "", description: "", start_time: "", end_time: "" });
      setLocationForm(EMPTY_LOCATION);
      load(1, upcomingOnly);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : t("common.noResults"));
    } finally { setCreateLoading(false); }
  };

  return (
    <>
    <PageHeader
      title={t("events.title")}
      icon={<EventIcon sx={{ color: "white", fontSize: 22 }} />}
      action={
        <FormControlLabel
          control={<Switch checked={upcomingOnly} onChange={(e) => setUpcomingOnly(e.target.checked)} size="small" />}
          label={<Typography variant="caption" sx={{ color: "white" }}>{t("events.upcomingOnly")}</Typography>}
          sx={{ mr: 0 }}
        />
      }
    />
    <Container maxWidth="sm" sx={{ py: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {list.length === 0 && !loading && (
        <Typography color="text.secondary" textAlign="center" py={6}>
          {t("events.noEvents")}
        </Typography>
      )}

      {list.map((ev) => {
        const currentRsvp = rsvpMap.get(ev.id);
        return (
          <Card
            key={ev.id}
            sx={{ mb: 2, cursor: "pointer", borderLeft: `4px solid ${STATUS_ACCENTS[ev.event_status] ?? "#6B7280"}` }}
            onClick={() => navigate(`/app/events/${ev.id}`)}
          >
            <CardContent>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Avatar sx={{ background: "linear-gradient(135deg, #DC2626 0%, #B91C2C 100%)" }}>
                  <EventIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>{ev.title}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    🗓 {formatInTimezone(ev.start_time, timezone)}
                    {ev.end_time && ` – ${formatInTimezone(ev.end_time, timezone)}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    {ev.description}
                  </Typography>
                  {ev.creator && (
                    <Typography variant="caption" color="text.secondary">
                      {t("events.by")} {ev.creator.first_name} {ev.creator.last_name}
                    </Typography>
                  )}
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={t(`events.${ev.event_status}`) || ev.event_status}
                      size="small"
                      sx={{ fontSize: "0.65rem", bgcolor: STATUS_ACCENTS[ev.event_status] ?? "#6B7280", color: "white", fontWeight: 700 }}
                    />
                    {ev.participants_count !== undefined && (
                      <Chip
                        label={`${ev.participants_count} ${t("events.going")}`}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 0.5, fontSize: "0.65rem" }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
            {user && (
              <CardActions sx={{ px: 2, pt: 0, flexWrap: "wrap", gap: 0.5 }}>
                {(["going", "interested", "maybe"] as EventRSVPStatus[]).map((s) => (
                  <Button
                    key={s}
                    size="small"
                    variant={currentRsvp === s ? "contained" : "outlined"}
                    color={RSVP_COLORS[s]}
                    onClick={(e) => { e.stopPropagation(); handleRSVP(ev.id, s); }}
                    sx={{ textTransform: "capitalize", fontSize: "0.7rem", ...(currentRsvp === s && { color: "white", "&.Mui-disabled": { color: "rgba(255,255,255,0.7)" } }) }}
                  >
                    {RSVP_LABELS[s] ?? s}
                  </Button>
                ))}
              </CardActions>
            )}
          </Card>
        );
      })}

      {loading && <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>}

      {!loading && page < totalPages && (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Button variant="outlined" onClick={() => { const n = page + 1; setPage(n); load(n, upcomingOnly); }}>
            {t("common.loadMore")}
          </Button>
        </Box>
      )}

      {user && (
        <Fab color="primary" aria-label="create event" onClick={handleOpenCreate}
          sx={{ position: "fixed", bottom: 72, right: 16 }}>
          <AddIcon />
        </Fab>
      )}

      {/* Create Event Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("events.createEvent")}</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 1 }}>{createError}</Alert>}
          <TextField label={t("events.titleLabel")} fullWidth margin="normal" value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            error={!!createFieldErrors.title} helperText={createFieldErrors.title} required />
          <TextField label={t("events.descriptionLabel")} fullWidth margin="normal" multiline rows={3} value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          <TextField label={t("events.startTime")} type="datetime-local" fullWidth margin="normal"
            value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            error={!!createFieldErrors.start_time} helperText={createFieldErrors.start_time} required />
          <TextField label={t("events.endTime")} type="datetime-local" fullWidth margin="normal"
            value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            error={!!createFieldErrors.end_time} helperText={createFieldErrors.end_time} />
          <LocationPicker value={locationForm} onChange={setLocationForm} required />
          {createFieldErrors.country && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>{createFieldErrors.country}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={createLoading}
            sx={{ color: "white", "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", bgcolor: "rgba(0,0,0,0.25)" } }}>
            {createLoading ? t("common.creating") : t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
}
